import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Menu,
  Modal,
  TextInput,
  Select,
  Textarea,
  Switch,
  NumberInput,
  MultiSelect,
  Loader,
  Alert,
  Tooltip,
  Card,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconClockPlay,
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPlayerPlay,
  IconPower,
  IconMail,
  IconCalendar,
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useCustomReports } from '@/hooks/api/useCustomReports';
import {
  useReportSchedules,
  useCreateReportSchedule,
  useUpdateReportSchedule,
  useDeleteReportSchedule,
  useToggleScheduleActive,
  useTriggerScheduledReport,
  type ReportSchedule,
  type CreateScheduleInput,
  type Recipient,
} from '@/hooks/api/useReportSchedules';

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'UTC', label: 'UTC' },
];

export function ReportAutomationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { organizationId } = useOrganization();
  const { data: schedules, isLoading } = useReportSchedules(organizationId);
  const { data: reports } = useCustomReports(organizationId);
  const createSchedule = useCreateReportSchedule();
  const updateSchedule = useUpdateReportSchedule();
  const deleteSchedule = useDeleteReportSchedule();
  const toggleActive = useToggleScheduleActive();
  const triggerReport = useTriggerScheduledReport();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ReportSchedule | null>(null);
  const [recipientInput, setRecipientInput] = useState('');

  // Handle reportId query param - auto-open modal with pre-selected report
  const reportIdFromUrl = searchParams.get('reportId');

  const form = useForm<{
    report_id: string;
    name: string;
    frequency: 'daily' | 'weekly' | 'monthly';
    day_of_week: string;
    day_of_month: number;
    time_of_day: string;
    timezone: string;
    recipients: string[];
    email_subject: string;
    email_intro: string;
    include_csv: boolean;
    include_summary: boolean;
  }>({
    initialValues: {
      report_id: '',
      name: '',
      frequency: 'weekly',
      day_of_week: '1',
      day_of_month: 1,
      time_of_day: '08:00',
      timezone: 'America/New_York',
      recipients: [],
      email_subject: '',
      email_intro: '',
      include_csv: true,
      include_summary: true,
    },
    validate: {
      report_id: (value) => (!value ? 'Select a report' : null),
      name: (value) => (!value ? 'Name is required' : null),
      recipients: (value) => (value.length === 0 ? 'Add at least one recipient' : null),
    },
  });

  // Auto-open modal when reportId is in URL
  useEffect(() => {
    if (reportIdFromUrl && reports && reports.length > 0) {
      const report = reports.find(r => r.id === reportIdFromUrl);
      if (report) {
        form.setValues({
          ...form.values,
          report_id: reportIdFromUrl,
          name: `${report.name} - Weekly Schedule`,
        });
        setModalOpen(true);
        setSearchParams({});
      }
    }
  }, [reportIdFromUrl, reports]);

  const openCreateModal = () => {
    form.reset();
    setEditingSchedule(null);
    setModalOpen(true);
  };

  const openEditModal = (schedule: ReportSchedule) => {
    setEditingSchedule(schedule);
    form.setValues({
      report_id: schedule.report_id,
      name: schedule.name,
      frequency: schedule.frequency,
      day_of_week: schedule.day_of_week?.toString() || '1',
      day_of_month: schedule.day_of_month || 1,
      time_of_day: schedule.time_of_day.slice(0, 5),
      timezone: schedule.timezone,
      recipients: schedule.recipients.map((r) => r.email),
      email_subject: schedule.email_subject || '',
      email_intro: schedule.email_intro || '',
      include_csv: schedule.include_csv,
      include_summary: schedule.include_summary,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!organizationId) return;

    const recipients: Recipient[] = values.recipients.map((email) => ({ email }));

    const input: CreateScheduleInput = {
      report_id: values.report_id,
      organization_id: organizationId,
      name: values.name,
      frequency: values.frequency,
      day_of_week: values.frequency === 'weekly' ? parseInt(values.day_of_week) : undefined,
      day_of_month: values.frequency === 'monthly' ? values.day_of_month : undefined,
      time_of_day: values.time_of_day + ':00',
      timezone: values.timezone,
      recipients,
      email_subject: values.email_subject || undefined,
      email_intro: values.email_intro || undefined,
      include_csv: values.include_csv,
      include_summary: values.include_summary,
    };

    try {
      if (editingSchedule) {
        await updateSchedule.mutateAsync({ id: editingSchedule.id, ...input });
        notifications.show({
          title: 'Schedule updated',
          message: 'Report schedule has been updated',
          color: 'green',
        });
      } else {
        await createSchedule.mutateAsync(input);
        notifications.show({
          title: 'Schedule created',
          message: 'Report schedule has been created',
          color: 'green',
        });
      }
      setModalOpen(false);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to save schedule',
        color: 'red',
      });
    }
  };

  const handleDelete = async (schedule: ReportSchedule) => {
    if (!organizationId) return;
    if (!confirm(`Delete schedule "${schedule.name}"?`)) return;

    try {
      await deleteSchedule.mutateAsync({ id: schedule.id, organizationId });
      notifications.show({
        title: 'Schedule deleted',
        message: 'Report schedule has been deleted',
        color: 'green',
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete schedule',
        color: 'red',
      });
    }
  };

  const handleToggleActive = async (schedule: ReportSchedule) => {
    try {
      await toggleActive.mutateAsync({ id: schedule.id, isActive: !schedule.is_active });
      notifications.show({
        title: schedule.is_active ? 'Schedule paused' : 'Schedule activated',
        message: schedule.is_active
          ? 'Report will not be sent until reactivated'
          : 'Report will be sent on schedule',
        color: 'green',
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update schedule',
        color: 'red',
      });
    }
  };

  const handleTrigger = async (schedule: ReportSchedule) => {
    try {
      await triggerReport.mutateAsync(schedule.id);
      notifications.show({
        title: 'Report triggered',
        message: 'Report is being generated and sent',
        color: 'blue',
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to trigger report',
        color: 'red',
      });
    }
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (!form.values.recipients.includes(email)) {
        form.setFieldValue('recipients', [...form.values.recipients, email]);
      }
      setRecipientInput('');
    }
  };

  const formatNextRun = (schedule: ReportSchedule) => {
    if (!schedule.is_active || !schedule.next_run_at) return 'Paused';
    const date = new Date(schedule.next_run_at);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatFrequency = (schedule: ReportSchedule) => {
    switch (schedule.frequency) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return `Weekly on ${DAYS_OF_WEEK.find((d) => d.value === schedule.day_of_week?.toString())?.label || ''}`;
      case 'monthly':
        return `Monthly on day ${schedule.day_of_month}`;
      default:
        return schedule.frequency;
    }
  };

  if (isLoading) {
    return (
      <Container fluid py="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={1}>Report Automations</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Schedule reports to run automatically and deliver results via email
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
            New Schedule
          </Button>
        </Group>

        {schedules && schedules.length > 0 ? (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Report</Table.Th>
                  <Table.Th>Frequency</Table.Th>
                  <Table.Th>Recipients</Table.Th>
                  <Table.Th>Next Run</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th w={60}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {schedules.map((schedule) => (
                  <Table.Tr key={schedule.id}>
                    <Table.Td fw={500}>{schedule.name}</Table.Td>
                    <Table.Td>{schedule.report?.name || 'Unknown'}</Table.Td>
                    <Table.Td>{formatFrequency(schedule)}</Table.Td>
                    <Table.Td>
                      <Tooltip label={schedule.recipients.map((r) => r.email).join(', ')}>
                        <Badge variant="light">{schedule.recipients.length} recipient(s)</Badge>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={schedule.is_active ? undefined : 'dimmed'}>
                        {formatNextRun(schedule)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {schedule.last_error ? (
                        <Tooltip label={schedule.last_error}>
                          <Badge color="red">Error</Badge>
                        </Tooltip>
                      ) : schedule.is_active ? (
                        <Badge color="green">Active</Badge>
                      ) : (
                        <Badge color="gray">Paused</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Menu withinPortal position="bottom-end" shadow="sm">
                        <Menu.Target>
                          <ActionIcon variant="subtle">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconPlayerPlay size={14} />}
                            onClick={() => handleTrigger(schedule)}
                          >
                            Run Now
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            onClick={() => openEditModal(schedule)}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconPower size={14} />}
                            onClick={() => handleToggleActive(schedule)}
                          >
                            {schedule.is_active ? 'Pause' : 'Activate'}
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => handleDelete(schedule)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        ) : (
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md" py="xl">
              <IconClockPlay size={64} stroke={1.5} style={{ color: 'var(--mantine-color-dimmed)' }} />
              <Title order={3} c="dimmed">
                No Scheduled Reports
              </Title>
              <Text size="sm" c="dimmed" ta="center" maw={400}>
                Create a schedule to automatically run reports and deliver them to your team via email.
              </Text>
              <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
                Create Your First Schedule
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingSchedule ? 'Edit Schedule' : 'New Report Schedule'}
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Select
              label="Report"
              placeholder="Select a report"
              data={
                reports?.map((r) => ({ value: r.id, label: r.name })) || []
              }
              {...form.getInputProps('report_id')}
              required
            />

            <TextInput
              label="Schedule Name"
              placeholder="e.g., Weekly Asset Summary"
              {...form.getInputProps('name')}
              required
            />

            <Select
              label="Frequency"
              data={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              {...form.getInputProps('frequency')}
            />

            {form.values.frequency === 'weekly' && (
              <Select
                label="Day of Week"
                data={DAYS_OF_WEEK}
                {...form.getInputProps('day_of_week')}
              />
            )}

            {form.values.frequency === 'monthly' && (
              <NumberInput
                label="Day of Month"
                min={1}
                max={28}
                {...form.getInputProps('day_of_month')}
              />
            )}

            <Group grow>
              <TextInput
                label="Time (24-hour)"
                type="time"
                {...form.getInputProps('time_of_day')}
              />
              <Select label="Timezone" data={TIMEZONES} {...form.getInputProps('timezone')} />
            </Group>

            <div>
              <Text size="sm" fw={500} mb={4}>
                Recipients
              </Text>
              <Group gap="xs" mb="xs">
                <TextInput
                  placeholder="email@example.com"
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  style={{ flex: 1 }}
                />
                <Button variant="light" onClick={addRecipient}>
                  Add
                </Button>
              </Group>
              {form.values.recipients.length > 0 && (
                <Group gap="xs">
                  {form.values.recipients.map((email) => (
                    <Badge
                      key={email}
                      variant="light"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={() =>
                            form.setFieldValue(
                              'recipients',
                              form.values.recipients.filter((e) => e !== email)
                            )
                          }
                        >
                          ×
                        </ActionIcon>
                      }
                    >
                      {email}
                    </Badge>
                  ))}
                </Group>
              )}
              {form.errors.recipients && (
                <Text size="xs" c="red" mt={4}>
                  {form.errors.recipients}
                </Text>
              )}
            </div>

            <TextInput
              label="Email Subject (optional)"
              placeholder="Leave blank for default"
              {...form.getInputProps('email_subject')}
            />

            <Textarea
              label="Email Introduction (optional)"
              placeholder="Custom message to include in the email"
              {...form.getInputProps('email_intro')}
            />

            <Group>
              <Switch
                label="Include CSV attachment"
                {...form.getInputProps('include_csv', { type: 'checkbox' })}
              />
              <Switch
                label="Include summary stats"
                {...form.getInputProps('include_summary', { type: 'checkbox' })}
              />
            </Group>

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createSchedule.isPending || updateSchedule.isPending}
              >
                {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
}
